import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Card,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDispatch } from 'react-redux';
import { getInsuranceDetails } from '../../../v2/Benefits/Beneficiary/APIs/action';
import { toast } from 'react-toastify';
import BenefitCard from '../../../v2/Benefits/Beneficiary/Components/BenefitCard';
import FirmDetails from '../../../v2/Benefits/Beneficiary/Components/FirmDetails';
import { CustomButton } from '../../../v2/MUI_custom_component';
import ExistingBeneficiaryModal from './Components/ExistingBeneficiaryModal';
import AddNpsBeneficiary from './Components/AddNpsBeneficiary';
import { useBackroute } from '../../../contexts/BackrouteContext';
import ExitModal from '../../Common/ExitModal';

const AddGapBeneficiary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 1200px)');
  const { updateBackroute, resetBackroute } = useBackroute();

  const insuranceType = location?.state?.insurance_type;
  const selectedCompanyEntity = location?.state?.selectedCompanyEntity;

  const [loader, setLoader] = useState(false);
  const [insuranceData, setInsuracedata] = useState(null);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [returnTransactionId, setReturnTransactionId] = useState(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const [hasFormActivity, setHasFormActivity] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    updateBackroute({ customHandler: () => setShowExitModal(true) });
    return () => resetBackroute();
  }, []);

  const fetchInsuranceDetails = async (type, entity) => {
    setLoader(true);
    const { data, error, message } = await getInsuranceDetails(type, entity);
    setLoader(false);
    if (!error) {
      setInsuracedata(data);
    } else {
      toast.error(message);
    }
  };

  const handleBeforeDigiLockerRedirect = (sessionId, formData) => {
    sessionStorage.setItem('digilocker_pending', JSON.stringify({
      insuranceType,
      selectedCompanyEntity,
      session_id: sessionId,
      formData,
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const transactionId = params.get('transaction_id');

    if (transactionId) {
      const saved = JSON.parse(sessionStorage.getItem('digilocker_pending') || '{}');
      sessionStorage.removeItem('digilocker_pending');
      window.history.replaceState({}, '', location.pathname);
      setReturnTransactionId(transactionId);
      if (saved.formData) {
        setSavedFormData(saved.formData);
      }
      if (saved.insuranceType && saved.selectedCompanyEntity) {
        fetchInsuranceDetails(saved.insuranceType, saved.selectedCompanyEntity);
      }
    } else if (insuranceType && selectedCompanyEntity) {
      fetchInsuranceDetails(insuranceType, selectedCompanyEntity);
    }
  }, []);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2 }}>
      {!loader ? (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid
            container
            spacing={2}
            sx={{ alignItems: 'stretch', mb: 2 }}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              <BenefitCard
                item={insuranceData?.benefit_card}
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <Card
                sx={{
                  boxShadow: 'none',
                  p: 2,
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
                }}
              >
                {insuranceData?.selected_firm &&
                  insuranceData?.selected_firm.map((detail, i) => (
                    <React.Fragment key={i}>
                      <FirmDetails detail={detail} />
                    </React.Fragment>
                  ))}
              </Card>
            </Grid>
            <Grid
              item
              xs={12}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <Typography variant='h2'>Add Beneficiary for NPS</Typography>
                {!hasFormActivity && !!insuranceData?.existing_beneficiaries?.length && (
                  <>
                    <CustomButton
                      variant='filled'
                      sx={{ width: { xs: '100%', md: 'auto' } }}
                      color={'primary'}
                      onClick={() => setShowExistingModal(true)}
                      label={'Choose Existing'}
                    />
                    <Typography
                      variant='p1'
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      or
                    </Typography>
                  </>
                )}
                <AddNpsBeneficiary
                  insuranceData={insuranceData}
                  owner_type_text={insuranceData?.owner_type_text}
                  owner_list={insuranceData?.owner_list}
                  confirmation_text_for_digilocker={insuranceData?.confirmation_text_for_digilocker}
                  returnTransactionId={returnTransactionId}
                  onBeforeDigiLockerRedirect={handleBeforeDigiLockerRedirect}
                  savedFormData={savedFormData}
                  onFormActivity={() => setHasFormActivity(true)}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <CircularProgress
            size={24}
            sx={{ color: '#995A00' }}
          />
        </Box>
      )}
      <ExistingBeneficiaryModal
        open={showExistingModal}
        onClose={() => setShowExistingModal(false)}
        data={insuranceData?.existing_beneficiaries}
        insuranceData={insuranceData}
      />
      <ExitModal
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={() => navigate('/beneficiary')}
        title="Are you sure you want to exit?"
        subtitle="This action cannot be undone. All data would be lost?"
        confirmLabel="Yes"
        cancelLabel="No"
      />
    </Box>
  );
};

export default AddGapBeneficiary;
